import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Crown, ArrowRight } from "lucide-react";
import { useAuth } from "../lib/auth";
import { formatApiErrorDetail } from "../lib/api";
import useSeo from "../lib/useSeo";

export default function LoginPage() {
    useSeo({
        title: "Enter the Chamber",
        description:
            "Sign in to EIGOO Books Hub to track your reading, unlock collector editions, and continue Sovereign Commander where you left off.",
    });
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from || "/";
    const [form, setForm] = useState({ email: "", password: "" });
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await login(form.email, form.password);
            navigate(from, { replace: true });
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <main data-testid="login-page" className="min-h-[80vh] flex items-center justify-center px-6 py-20">
            <div className="w-full max-w-md">
                <div className="text-center mb-10">
                    <Crown className="w-7 h-7 text-[#D4AF37] mx-auto mb-4" />
                    <div className="eyebrow mb-2">EIGOO Books</div>
                    <h1 className="font-serif text-4xl text-white">Enter the chamber</h1>
                </div>

                <form
                    onSubmit={submit}
                    className="frame-gold p-8 space-y-5"
                    data-testid="login-form"
                >
                    <div className="space-y-1.5">
                        <label className="eyebrow text-[10px] block">Email</label>
                        <input
                            data-testid="login-email"
                            type="email"
                            required
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            className="w-full bg-[#141414] border px-4 py-3 text-white"
                            style={{ borderColor: "rgba(212,175,55,0.2)" }}
                        />
                    </div>
                    <div className="space-y-1.5">
                        <label className="eyebrow text-[10px] block">Password</label>
                        <input
                            data-testid="login-password"
                            type="password"
                            required
                            value={form.password}
                            onChange={(e) => setForm({ ...form, password: e.target.value })}
                            className="w-full bg-[#141414] border px-4 py-3 text-white"
                            style={{ borderColor: "rgba(212,175,55,0.2)" }}
                        />
                    </div>

                    {error && (
                        <div
                            data-testid="login-error"
                            className="border border-[#8A0303]/50 bg-[#8A0303]/10 px-4 py-3 text-sm text-[#cd6a6a]"
                        >
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        data-testid="login-submit"
                        disabled={loading}
                        className="btn-gold w-full justify-center disabled:opacity-50"
                    >
                        {loading ? "Verifying…" : "Enter"}
                        <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                </form>

                <p className="text-center text-sm text-[#A1A1AA] mt-6">
                    No sigil yet?{" "}
                    <Link
                        to="/register"
                        data-testid="link-register"
                        className="text-[#D4AF37] hover:text-[#E6D59A]"
                    >
                        Begin reading →
                    </Link>
                </p>
            </div>
        </main>
    );
}
